import type { ParticipationPoint } from '@api/types'
import { useI18n } from '@i18n/I18nProvider'
import { formatPeriodStart } from './period'

// Altura de cada barra en % del máximo. Un periodo con avisos nunca baja del 2 % (se ve).
export const barHeights = (values: readonly number[]): number[] => {
	const max = Math.max(0, ...values)
	return values.map((v) => (max === 0 || v <= 0 ? 0 : Math.max(2, Math.round((v / max) * 100))))
}

// Avisos por periodo: una sola serie (el título la nombra, sin leyenda), barras finas con el
// extremo redondeado sobre la línea base, valor al pasar o enfocar cada barra y los mismos
// datos en una tabla desplegable.
export const ParticipationChart = ({ points }: { points: readonly ParticipationPoint[] }) => {
	const { t, locale } = useI18n()
	const heights = barHeights(points.map((p) => p.reports))
	const max = Math.max(0, ...points.map((p) => p.reports))
	const first = points[0]
	const last = points.at(-1)
	const describe = (p: ParticipationPoint) =>
		`${t('participation.week', { date: formatPeriodStart(p.period, locale) })}: ${p.reports} · ${t('participation.participants')}: ${p.participants}`
	return (
		<div>
			<div className="flex gap-2">
				<span
					className="flex h-40 flex-col justify-between text-right text-xs text-earth-600"
					aria-hidden="true"
				>
					<span>{max}</span>
					<span>0</span>
				</span>
				<ol className="flex h-40 flex-1 items-end gap-0.5 border-b border-earth-300">
					{points.map((point, i) => (
						<li key={point.period} className="group relative flex h-full flex-1 items-end justify-center">
							<span
								// biome-ignore lint/a11y/noNoninteractiveTabindex: cada barra se enfoca para leer su valor con teclado
								tabIndex={0}
								aria-label={describe(point)}
								className="peer flex h-full w-full items-end justify-center rounded-sm outline-offset-0 hover:bg-forest-50 focus-visible:bg-forest-50"
							>
								<span
									className="w-full max-w-6 rounded-t bg-forest-700"
									style={{ height: `${heights[i]}%` }}
								/>
							</span>
							<span
								role="tooltip"
								className="pointer-events-none absolute bottom-full z-10 mb-1 hidden w-max max-w-48 rounded-lg bg-forest-900 px-2 py-1 text-xs text-white peer-hover:block peer-focus-visible:block"
							>
								{formatPeriodStart(point.period, locale)}: {point.reports}{' '}
								{t('participation.reports').toLowerCase()}
								<br />
								{point.participants} {t('participation.participants').toLowerCase()}
							</span>
						</li>
					))}
				</ol>
			</div>
			<div className="ml-6 flex justify-between pt-1 text-xs text-earth-600" aria-hidden="true">
				<span>{first && formatPeriodStart(first.period, locale)}</span>
				<span>{last && formatPeriodStart(last.period, locale)}</span>
			</div>
			<details className="mt-3">
				<summary className="min-h-12 cursor-pointer py-3 font-semibold text-forest-800">
					{t('participation.table')}
				</summary>
				<table className="w-full text-left text-sm">
					<thead>
						<tr className="border-b border-earth-300">
							<th className="py-2 font-semibold">{t('participation.period')}</th>
							<th className="py-2 text-right font-semibold">{t('participation.reports')}</th>
							<th className="py-2 text-right font-semibold">{t('participation.participants')}</th>
							<th className="py-2 text-right font-semibold">{t('participation.trees')}</th>
						</tr>
					</thead>
					<tbody>
						{points.map((p) => (
							<tr key={p.period} className="border-b border-earth-100">
								<td className="py-2">{formatPeriodStart(p.period, locale)}</td>
								<td className="py-2 text-right tabular-nums">{p.reports}</td>
								<td className="py-2 text-right tabular-nums">{p.participants}</td>
								<td className="py-2 text-right tabular-nums">{p.trees}</td>
							</tr>
						))}
					</tbody>
				</table>
			</details>
		</div>
	)
}
